import { LitElement, html, nothing } from 'lit'
import { customElement } from 'lit/decorators.js'
import { keyed } from 'lit/directives/keyed.js'
import { consume } from '@lit/context'
import type { AlignItems, AreaPaddingCfg, AreaVerticalAlign, FlexDirection, JustifyContent } from 'components/shared/base-product-tile/base-product-tile-types.js'
import { style } from 'components/shared/product-tile-configurator/components/tile-configurator-zone-panel.style.js'
import { AREA_LABELS, IMAGE_AREA_LABEL, areaCrumbFor, panelHeaderStyle, renderPanelHeader, viewCrumb, zoneLabelFor } from './panel-header.js'
import { isMobileConfigMode, layoutKindFor, type ConfigMode, type Section } from 'components/shared/product-tile-configurator/editor-state.js'
import { displayConfigForMode } from 'components/shared/product-tile-configurator/image-config-mapper.js'
import {
  areaFor,
  AreaPaddingContext,
  CanvasStateContext,
  ImageConfigContext,
  ImageFocusContext,
  MobileModeContext,
  SelectedAreaContext,
  SelectedWidgetContext,
  TileConfiguratorActionsContext,
  type AreaPaddingContextType,
  type CanvasStateContextType,
  type DynamicArea,
  type DynamicAreaSettings,
  type ImageConfigContextType,
  type ImageFlowSlot,
  type ImageFocusContextType,
  type MobileModeContextType,
  type SelectedAreaContextType,
  type SelectedWidgetContextType,
  type TileConfiguratorActionsContextType,
} from 'components/shared/product-tile-configurator/context.js'

const DIRECTION_OPTIONS: FlexDirection[] = ['column', 'row']
const JUSTIFY_OPTIONS: JustifyContent[] = ['flex-start', 'center', 'flex-end', 'space-between', 'space-around', 'space-evenly']
const ALIGN_ITEMS_OPTIONS: AlignItems[] = ['flex-start', 'center', 'flex-end', 'stretch']

// Explains the canvas's three selection outline colors (see tile-configurator-canvas.style.ts's
// "Selection outline styling" note) — the swatches reuse the same colors.
const SELECTION_LEGEND: Array<{ kind: 'area' | 'zone' | 'widget'; label: string; description: string }> = [
  { kind: 'area', label: 'Area', description: 'padding' },
  { kind: 'zone', label: 'Zone', description: 'padding & flex layout' },
  { kind: 'widget', label: 'Widget', description: 'component settings' },
]

// Mirrors the --cs-spacing-* scale (shared/src/styles/spacing.style.ts) — the gap field only
// ever offers these design-system tokens, never an arbitrary CSS length.
const GAP_TOKENS: Array<{ token: string; fallback: string }> = [
  { token: '00', fallback: '1px' },
  { token: '01', fallback: '2px' },
  { token: '02', fallback: '3px' },
  { token: '03', fallback: '4px' },
  { token: '04', fallback: '5px' },
  { token: '05', fallback: '10px' },
  { token: '06', fallback: '15px' },
  { token: '07', fallback: '20px' },
  { token: '08', fallback: '30px' },
  { token: '09', fallback: '40px' },
  { token: '10', fallback: '50px' },
  { token: '11', fallback: '60px' },
  { token: '12', fallback: '75px' },
  { token: '13', fallback: '100px' },
]

// The fallback here isn't optional decoration — --cs-spacing-* is defined by
// the consuming application's theme, not this component library, so in any
// environment that doesn't set it (e.g. Storybook) an unresolved var() with
// no fallback is invalid at computed-value time, silently dropping the whole
// `gap` declaration. Always include it, matching how csSpacing.* is itself
// defined (shared/src/styles/spacing.style.ts).
const GAP_OPTIONS: Array<{ value: string; label: string }> = GAP_TOKENS.map(({ token, fallback }) => ({
  value: `var(--cs-spacing-${token}, ${fallback})`,
  label: `cs-spacing-${token} (${fallback})`,
}))

// A zone with no explicit gap configured still renders with a real gap — see
// base-product-tile.style.ts's .product-tile-body/.product-tile-footer .zone
// (hardcoded to spacing05, 10px) — so the select reflects that as its own
// selected value instead of a separate, misleading "no gap" option.
const DEFAULT_GAP_VALUE = 'var(--cs-spacing-05, 10px)'

type PaddingKey = 'paddingTop' | 'paddingRight' | 'paddingBottom' | 'paddingLeft'
const PADDING_KEYS: PaddingKey[] = ['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft']
const PADDING_LABELS: Record<PaddingKey, string> = {
  paddingTop: 'Top',
  paddingRight: 'Right',
  paddingBottom: 'Bottom',
  paddingLeft: 'Left',
}

// Same design-token scale as the Gap field, plus two options Gap doesn't need: an explicit
// "not set" option — unset genuinely means "no override here" (falls through to whatever real
// default already applies, e.g. spacing05 for productInfo/orderActions — see
// tile-configurator-canvas.ts's effectivePaddingStyleFor), not a stand-in for a chosen value —
// and an explicit "0" (csSpacing.spacingnone), distinct from "not set": an author who actually
// wants zero padding must be able to say so and have it render as a real, visible 0, not just
// fall back to whatever the unconfigured default happens to be.
const PADDING_VALUE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: '—' },
  { value: '0', label: 'cs-spacing-none (0)' },
  ...GAP_OPTIONS,
]

// Every side is entered manually — no smart-fill between fields. Editing one side only ever
// changes that side. Generic (not just AreaPaddingCfg) so productInfo/orderActions' wider
// DynamicAreaSettings (padding + row/column gap) round-trips through untouched — the image
// area still only ever passes a plain AreaPaddingCfg, so T narrows to that there.
const setPaddingValue = <T extends AreaPaddingCfg>(current: T, key: PaddingKey, value: string): T => {
  const next: T = { ...current }
  if (value) next[key] = value
  else delete next[key]
  return next
}

// Row/column gap for the whole productInfo/orderActions area — distinct from a zone's own Gap
// field (that's between a zone's own widgets; this is between rows, and between the up to 2
// zones in a split row — see DynamicSectionCfg's rowGap/columnGap). No image equivalent: the
// image area has no rows/columns to space out, just fixed overlay places.
type GapKey = 'rowGap' | 'columnGap'
const GAP_KEYS: GapKey[] = ['rowGap', 'columnGap']
const GAP_LABELS: Record<GapKey, string> = {
  rowGap: 'Row gap',
  columnGap: 'Column gap',
}

// Same as the per-zone Gap field: no "not set" option, always a real selected value — an
// area's rows/split-zones always render with a real gap already (spacing05 by default), so
// there's no meaningful "no gap here" state to represent separately from picking spacing05
// itself.
const setGapValue = (current: DynamicAreaSettings, key: GapKey, value: string): DynamicAreaSettings => ({ ...current, [key]: value })

// List view only — see DynamicSectionCfg.verticalAlign. No "not set" option, same reasoning as
// the gap fields: unset already renders as flex-start (rows at the top).
const VERTICAL_ALIGN_OPTIONS: Array<{ value: AreaVerticalAlign; label: string }> = [
  { value: 'flex-start', label: 'Top' },
  { value: 'center', label: 'Center' },
  { value: 'flex-end', label: 'Bottom' },
  { value: 'space-between', label: 'Space between' },
]

// Mobile list stacks productInfo over orderActions (see base-product-tile.ts's stacksAreasAtTop):
// orderActions' alignment there only decides whether it sits at the bottom of the tile (the
// default, productInfo grows) or directly under productInfo — anything but flex-start is Bottom.
const MOBILE_ORDER_ACTIONS_ALIGN_OPTIONS: Array<{ value: AreaVerticalAlign; label: string }> = [
  { value: 'flex-start', label: 'Top' },
  { value: 'flex-end', label: 'Bottom' },
]

const omitPadding = <T extends AreaPaddingCfg>(cfg: T | undefined): Omit<T, PaddingKey> => {
  const next = { ...(cfg as AreaPaddingCfg) }
  PADDING_KEYS.forEach(key => delete next[key])
  return next as Omit<T, PaddingKey>
}

// The left sidebar's single purpose: dynamic-zone layout settings (direction/justify/align/
// wrap/gap). Always visible, independent of the widget library (now its own top bar) — a
// zone stays "selected" here (and its settings shown) for as long as the selection's sectionId
// resolves to a dynamic zone, whether a bare zone or a widget inside it is what's actually
// selected (see SelectedZoneRef — widgetId doesn't affect which zone's settings this shows).
@customElement('csc-tile-configurator-zone-panel')
export class CSCTileConfiguratorZonePanel extends LitElement {
  static override styles = [panelHeaderStyle, style]

  @consume({ context: CanvasStateContext, subscribe: true })
  private sectionsByMode!: CanvasStateContextType

  @consume({ context: SelectedWidgetContext, subscribe: true })
  private selectedZone!: SelectedWidgetContextType

  @consume({ context: SelectedAreaContext, subscribe: true })
  private selectedArea!: SelectedAreaContextType

  @consume({ context: ImageFocusContext, subscribe: true })
  private imageFocused!: ImageFocusContextType

  @consume({ context: AreaPaddingContext, subscribe: true })
  private areaPaddingByMode!: AreaPaddingContextType

  @consume({ context: ImageConfigContext, subscribe: true })
  private imageConfig!: ImageConfigContextType

  @consume({ context: MobileModeContext, subscribe: true })
  private mobileMode!: MobileModeContextType

  @consume({ context: TileConfiguratorActionsContext })
  private actions!: TileConfiguratorActionsContextType

  private getSelectedDynamicZone(): { mode: ConfigMode; area: DynamicArea; section: Section } | null {
    const selectedZone = this.selectedZone
    if (!selectedZone) return null
    const section = this.sectionsByMode[selectedZone.mode].find(s => s.id === selectedZone.sectionId)
    const area = section ? areaFor(section.dropAreaType) : null
    return section && area ? { mode: selectedZone.mode, area, section } : null
  }

  // Image-overlay places (top-left, middle-center, etc.) are zones too — just fixed absolute
  // slots with no flex layout of their own, so there's nothing to configure or remove. They
  // still get identified here (mode + area + zone name) so the panel isn't misleadingly empty
  // while one is selected.
  private getSelectedImageOverlayZone(): { mode: ConfigMode; section: Section } | null {
    const selectedZone = this.selectedZone
    if (!selectedZone) return null
    const section = this.sectionsByMode[selectedZone.mode].find(s => s.id === selectedZone.sectionId)
    return section?.dropAreaType === 'image-overlay' ? { mode: selectedZone.mode, section } : null
  }

  // above/below — unlike a productInfo/orderActions zone, there's no separate "select the
  // whole area" vs. "select this one zone within it" distinction to make here (see
  // SelectedAreaRef): they're always exactly one zone, so selecting the zone (via the normal
  // SelectedWidgetContext/selectWidget path, same as any other zone) already means selecting
  // the whole area. No areaFor()-style lookup either — the section id itself (only ever
  // 'above'/'below') is the slot.
  private getSelectedImageFlowZone(): { mode: ConfigMode; slot: ImageFlowSlot; section: Section } | null {
    const selectedZone = this.selectedZone
    if (!selectedZone) return null
    const section = this.sectionsByMode[selectedZone.mode].find(s => s.id === selectedZone.sectionId)
    if (section?.dropAreaType !== 'image-flow') return null
    return { mode: selectedZone.mode, slot: section.id as ImageFlowSlot, section }
  }

  private handleZoneDirectionChange(mode: ConfigMode, sectionId: string, event: Event) {
    this.actions.updateZoneStyle(mode, sectionId, { direction: (event.target as HTMLSelectElement).value as FlexDirection })
  }

  private handleZoneJustifyContentChange(mode: ConfigMode, sectionId: string, event: Event) {
    this.actions.updateZoneStyle(mode, sectionId, { justifyContent: (event.target as HTMLSelectElement).value as JustifyContent })
  }

  private handleZoneAlignItemsChange(mode: ConfigMode, sectionId: string, event: Event) {
    this.actions.updateZoneStyle(mode, sectionId, { alignItems: (event.target as HTMLSelectElement).value as AlignItems })
  }

  private handleZoneGapChange(mode: ConfigMode, sectionId: string, event: Event) {
    const value = (event.target as HTMLSelectElement).value
    this.actions.updateZoneStyle(mode, sectionId, { gap: value || undefined })
  }

  private handleZoneWrapToggle(mode: ConfigMode, sectionId: string, event: Event) {
    const checked = (event.currentTarget as HTMLInputElement & { checked: boolean }).checked
    this.actions.updateZoneStyle(mode, sectionId, { wrap: checked })
  }

  private handleHideUntilHoverToggle(mode: ConfigMode, sectionId: string, event: Event) {
    const checked = (event.currentTarget as HTMLInputElement & { checked: boolean }).checked
    this.actions.updateZoneStyle(mode, sectionId, { hideUntilHover: checked })
  }

  private renderEmptyState() {
    return html`
      <div class="zone-panel-empty">
        <cse-text variant="body-s">Select an area, zone or widget to edit its settings.</cse-text>
        <ul class="selection-legend">
          ${SELECTION_LEGEND.map(item => html`
            <li class="selection-legend-item">
              <span class="selection-legend-swatch selection-legend-swatch-${item.kind}"></span>
              <cse-text variant="body-s"><strong>${item.label}</strong> · ${item.description}</cse-text>
            </li>
          `)}
        </ul>
      </div>
    `
  }

  override render() {
    // A selected widget's settings render in csc-tile-configurator-sidebar instead, which shares
    // the right panel with this component — only one of the two ever shows at a time.
    if (this.selectedZone?.widgetId) return nothing

    // Keyed per selection: cse-select only re-syncs its shown label when its options change, not
    // when only its value attribute does, so a reused select would keep showing the previous
    // selection's label (e.g. "Top" for an area whose value is center).
    const dynamicZone = this.getSelectedDynamicZone()
    if (dynamicZone) return keyed(`zone:${dynamicZone.mode}:${dynamicZone.section.id}`, this.renderDynamicZone(dynamicZone))

    const imageFlowZone = this.getSelectedImageFlowZone()
    if (imageFlowZone) return keyed(`zone:${imageFlowZone.mode}:${imageFlowZone.section.id}`, this.renderImageFlowZone(imageFlowZone))

    const imageOverlayZone = this.getSelectedImageOverlayZone()
    if (imageOverlayZone) return keyed(`zone:${imageOverlayZone.mode}:${imageOverlayZone.section.id}`, this.renderImageOverlayZone(imageOverlayZone))

    if (this.selectedArea) return keyed(`area:${this.selectedArea.mode}:${this.selectedArea.area}`, this.renderAreaPadding(this.selectedArea.mode, this.selectedArea.area))

    if (this.imageFocused) return keyed(`image:${this.imageFocused}`, this.renderImageAreaPadding(this.imageFocused))

    // Mobile preview is fully inert (see MobileModeContextType) — selection can never happen
    // while it's on, so this empty state would otherwise always show. Rendering nothing
    // instead of the "No area or zone selected." message avoids stating the obvious for a
    // state the toggle itself already communicates.
    return this.mobileMode ? nothing : this.renderEmptyState()
  }

  // `displayDefault` only affects what an untouched select visually shows — the value actually
  // passed to setPaddingValue is always the raw, real state (`padding`), so an edit to one side
  // never "locks in" the shown default for any side the author hasn't actually touched.
  private renderAreaPaddingEditor<T extends AreaPaddingCfg>(padding: T, onChange: (next: T) => void, displayDefault?: string) {
    const renderInput = (key: PaddingKey) => html`
      <cse-form-field class="area-padding-${key.replace(/^padding/, '').toLowerCase()}">
        <cse-label slot="label">${PADDING_LABELS[key]}</cse-label>
        <cse-select
          value=${padding[key] ?? displayDefault ?? ''}
          @change=${(e: Event) => onChange(setPaddingValue(padding, key, (e.target as HTMLSelectElement).value))}>
          ${PADDING_VALUE_OPTIONS.map(option => html`<cse-option value=${option.value}>${option.label}</cse-option>`)}
        </cse-select>
      </cse-form-field>
    `
    return html`
      <div class="area-padding-stack">
        ${PADDING_KEYS.map(key => renderInput(key))}
      </div>
    `
  }

  private renderAreaGapEditor(settings: DynamicAreaSettings, onChange: (next: DynamicAreaSettings) => void) {
    const renderInput = (key: GapKey) => html`
      <cse-form-field class="area-gap-${key === 'rowGap' ? 'row' : 'column'}">
        <cse-label slot="label">${GAP_LABELS[key]}</cse-label>
        <cse-select
          value=${settings[key] ?? DEFAULT_GAP_VALUE}
          @change=${(e: Event) => onChange(setGapValue(settings, key, (e.target as HTMLSelectElement).value))}>
          ${GAP_OPTIONS.map(option => html`<cse-option value=${option.value}>${option.label}</cse-option>`)}
        </cse-select>
      </cse-form-field>
    `
    return html`
      <div class="area-gap-stack">
        ${GAP_KEYS.map(key => renderInput(key))}
      </div>
    `
  }

  private renderAreaPadding(mode: ConfigMode, area: DynamicArea) {
    const settings = this.areaPaddingByMode[mode][area] ?? {}
    return html`
      ${renderPanelHeader([viewCrumb(mode, this.mobileMode)], AREA_LABELS[area], 'area')}
      <!-- Its own class, NOT zone-settings-fields — that class means "a real dynamic zone is
           selected" (renderDynamicZone) and several tests assert it's absent for an area
           selection; sharing it here made an area's own padding editor register as if a zone
           were selected too. Same layout styling (see the stylesheet), just a distinct class. -->
      <div class="area-padding-fields">
        <cse-text variant="caption-s">Padding</cse-text>
        <!-- productInfo/orderActions already render with a real spacing05 (10px) padding when
             unconfigured (base-product-tile.style.ts's .body/.footer default) — shown
             pre-selected here so the inputs reflect what's actually already applied. -->
        ${this.renderAreaPaddingEditor(settings, next => this.actions.updateAreaPadding(mode, area, next), DEFAULT_GAP_VALUE)}
      </div>
      <!-- Row/column gap — productInfo/orderActions only, no image equivalent (see GapKey
           above), so this block lives only in renderAreaPadding, not renderImageAreaPadding. -->
      <div class="area-gap-fields">
        <cse-text variant="caption-s">Gap</cse-text>
        ${this.renderAreaGapEditor(settings, next => this.actions.updateAreaGap(mode, area, next))}
      </div>
      ${layoutKindFor(mode) === 'list' ? html`
        <div class="area-alignment-fields">
          <cse-text variant="caption-s">Alignment</cse-text>
          ${this.renderVerticalAlignField(mode, area, settings)}
        </div>
      ` : nothing}
    `
  }

  private renderVerticalAlignField(mode: ConfigMode, area: DynamicArea, settings: DynamicAreaSettings) {
    const mobileOrderActions = isMobileConfigMode(mode) && area === 'orderActions'
    const options = mobileOrderActions ? MOBILE_ORDER_ACTIONS_ALIGN_OPTIONS : VERTICAL_ALIGN_OPTIONS
    const value = mobileOrderActions
      ? (settings.verticalAlign === 'flex-start' ? 'flex-start' : 'flex-end')
      : settings.verticalAlign ?? 'flex-start'
    const mobileProductInfo = isMobileConfigMode(mode) && area === 'productInfo'
    const helpText = mobileOrderActions
      ? 'Bottom keeps order actions at the bottom of the tile; Top places them directly under product info.'
      : mobileProductInfo
        ? 'Aligns this area\'s rows within the full height of the tile. Always top when order actions is set to Top.'
        : 'Aligns this area\'s rows within the full height of the tile.'
    return html`
      <cse-form-field class="area-vertical-align">
        <cse-label slot="label">Vertical alignment</cse-label>
        <cse-select
          value=${value}
          @change=${(e: Event) => this.actions.updateAreaAlignment(mode, area, { ...settings, verticalAlign: (e.target as HTMLSelectElement).value as AreaVerticalAlign })}>
          ${options.map(option => html`<cse-option value=${option.value}>${option.label}</cse-option>`)}
        </cse-select>
        <span slot="help-text">${helpText}</span>
      </cse-form-field>
    `
  }

  private renderImageAreaPadding(mode: ConfigMode) {
    const padding = displayConfigForMode(this.imageConfig, mode)
    return html`
      ${renderPanelHeader([viewCrumb(mode, this.mobileMode)], IMAGE_AREA_LABEL, 'area')}
      <div class="area-padding-fields">
        <cse-text variant="caption-s">Padding</cse-text>
        ${this.renderAreaPaddingEditor(padding, next => this.actions.updateImageConfig({
          [mode]: { ...omitPadding(this.imageConfig[mode] ?? {}), ...next },
        }))}
      </div>
      <div class="image-settings-fields">
        <cse-text variant="caption-s">Image</cse-text>
        <cse-form-field>
          <cse-label slot="label">Enable hover image</cse-label>
          <cse-toggle
            ?checked=${Boolean(this.imageConfig.enableHoverImage)}
            @change=${(e: Event) => this.actions.updateImageConfig({ enableHoverImage: (e.currentTarget as HTMLInputElement & { checked: boolean }).checked })}>
          </cse-toggle>
          <span slot="help-text">Applies to all layout modes (grid, list).</span>
        </cse-form-field>
      </div>
    `
  }

  // Shared by both a real productInfo/orderActions zone (renderDynamicZone) and an above/below
  // image-flow zone (renderImageFlowZone) — the fields themselves don't need a DynamicArea, see
  // updateZoneStyle.
  private renderZoneSettingsFields(mode: ConfigMode, section: Section) {
    return html`
      <div class="zone-settings-fields">
        <cse-form-field>
          <cse-label slot="label">Direction</cse-label>
          <cse-select value=${section.direction ?? 'column'} @change=${(e: Event) => this.handleZoneDirectionChange(mode, section.id, e)}>
            ${DIRECTION_OPTIONS.map(option => html`<cse-option value=${option}>${option}</cse-option>`)}
          </cse-select>
        </cse-form-field>

        <cse-form-field>
          <cse-label slot="label">Justify content</cse-label>
          <cse-select value=${section.justifyContent ?? 'flex-start'} @change=${(e: Event) => this.handleZoneJustifyContentChange(mode, section.id, e)}>
            ${JUSTIFY_OPTIONS.map(option => html`<cse-option value=${option}>${option}</cse-option>`)}
          </cse-select>
        </cse-form-field>

        <cse-form-field>
          <cse-label slot="label">Align items</cse-label>
          <cse-select value=${section.alignItems ?? 'stretch'} @change=${(e: Event) => this.handleZoneAlignItemsChange(mode, section.id, e)}>
            ${ALIGN_ITEMS_OPTIONS.map(option => html`<cse-option value=${option}>${option}</cse-option>`)}
          </cse-select>
        </cse-form-field>

        <cse-form-field>
          <cse-label slot="label">Wrap</cse-label>
          <cse-toggle ?checked=${Boolean(section.wrap)} @change=${(e: Event) => this.handleZoneWrapToggle(mode, section.id, e)}></cse-toggle>
        </cse-form-field>

        <cse-form-field>
          <cse-label slot="label">Gap</cse-label>
          <cse-select value=${section.gap ?? DEFAULT_GAP_VALUE} @change=${(e: Event) => this.handleZoneGapChange(mode, section.id, e)}>
            ${GAP_OPTIONS.map(option => html`<cse-option value=${option.value}>${option.label}</cse-option>`)}
          </cse-select>
          <span slot="help-text">Design-system spacing tokens only. Applies between widgets in this zone.</span>
        </cse-form-field>
      </div>
    `
  }

  private renderDynamicZone(dynamicZone: { mode: ConfigMode; area: DynamicArea; section: Section }) {
    const { mode, section } = dynamicZone

    // No "Remove zone" action here any more — the canvas's own .zone-remove-affordance
    // (tile-configurator-canvas.ts/.style.ts) already covers it once the zone is selected,
    // so this would've been a second, redundant control for the same action.
    return html`
      ${renderPanelHeader([viewCrumb(mode, this.mobileMode), areaCrumbFor(mode, section, this.actions)], zoneLabelFor(section), 'zone')}
      ${this.renderZoneSettingsFields(mode, section)}
    `
  }

  // Image-overlay places (top-left, middle-center, etc.) have no flex layout of their own to
  // configure — the only per-place setting is the opt-in hover-reveal (see Section.hideUntilHover
  // / ImageOverlayHoverConfig), which previously lived on csc-compare-checkbox/
  // csc-product-tile-add-to-order-template themselves as a hideUntilHover property.
  private renderImageOverlayZone(imageOverlayZone: { mode: ConfigMode; section: Section }) {
    const { mode, section } = imageOverlayZone
    return html`
      ${renderPanelHeader([viewCrumb(mode, this.mobileMode), areaCrumbFor(mode, section, this.actions)], zoneLabelFor(section), 'zone')}
      <div class="zone-settings-fields">
        ${isMobileConfigMode(mode) ? html`
          <cse-text class="zone-settings-note" variant="body-s">Hide until hover isn't available on mobile — touch screens can't hover.</cse-text>
        ` : html`
          <cse-form-field>
            <cse-label slot="label">Hide until hover</cse-label>
            <cse-toggle ?checked=${Boolean(section.hideUntilHover)} @change=${(e: Event) => this.handleHideUntilHoverToggle(mode, section.id, e)}></cse-toggle>
            <span slot="help-text">Hides this zone's widgets until the tile is hovered or focused.</span>
          </cse-form-field>
        `}
      </div>
    `
  }

  // above/below: same zone-settings fields a productInfo/orderActions zone gets, plus their
  // own padding (like a productInfo/orderActions *area* gets) — but no remove-zone button
  // (always exactly one zone, never removable) and no row/column gap editor (no row/column
  // layout to space out — see ImageFlowSlot).
  private renderImageFlowZone(imageFlowZone: { mode: ConfigMode; slot: ImageFlowSlot; section: Section }) {
    const { mode, slot, section } = imageFlowZone
    const settings = this.areaPaddingByMode[mode][slot] ?? {}
    return html`
      ${renderPanelHeader([viewCrumb(mode, this.mobileMode), areaCrumbFor(mode, section, this.actions)], zoneLabelFor(section), 'zone')}
      ${this.renderZoneSettingsFields(mode, section)}
      <div class="area-padding-fields">
        <cse-text variant="caption-s">Padding</cse-text>
        <!-- No displayDefault — unlike productInfo/orderActions, above/below have no real
             non-zero unconfigured default (no theme padding backs them, unlike <cse-card>'s
             own body/footer) — same as the image area's own padding editor. -->
        ${this.renderAreaPaddingEditor(settings, next => this.actions.updateAreaPadding(mode, slot, next))}
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-tile-configurator-zone-panel': CSCTileConfiguratorZonePanel
  }
}
